"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.EnvironmentManagerProvider = exports.EnvironmentManagerContext = void 0;
var _react = _interopRequireWildcard(require("react"));
var _hooks = require("../../hooks");
var _useAudiencePickerProvider = require("../AudiencePicker/provider/useAudiencePickerProvider");
var _ScoreTypes = require("./types/ScoreTypes");
var _EnvironmentManager = require("./EnvironmentManager.labels");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const EnvironmentManagerContext = exports.EnvironmentManagerContext = /*#__PURE__*/_react.default.createContext({});
const EnvironmentManagerProvider = _ref => {
  let {
    children
  } = _ref;
  // Hooks
  const getLabel = (0, _hooks.useLabels)({
    labels: _EnvironmentManager.labels
  });
  const userSessionData = (0, _hooks.useSessionData)();
  const {
    pxToRem
  } = (0, _hooks.useStylesUtils)();

  // UI states
  const [sliderView, setSliderView] = (0, _react.useState)();
  const [preloaderMode, setPreloaderMode] = (0, _react.useState)();

  // API Data states
  const [schoolYear, setSchoolYear] = (0, _react.useState)();
  const [schoolTeachers, setSchoolTeachers] = (0, _react.useState)([]);
  const [userGroups, setUserGroups] = (0, _react.useState)([]);
  const [userEnvironments, setUserEnvironments] = (0, _react.useState)([]);

  // EnvironmentData states
  const [environmentIds, setEnvironmentIds] = (0, _react.useState)();
  const [environmentTitle, setEnvironmentTitle] = (0, _react.useState)('');
  const [environmentSchoolId, setEnvironmentSchoolId] = (0, _react.useState)();
  const [environmentDates, setEnvironmentDates] = (0, _react.useState)();
  const [environmentProductInfo, setEnvironmentProductInfo] = (0, _react.useState)();
  const [environmentTeachers, setEnvironmentTeachers] = (0, _react.useState)([]);
  const [environmentAudience, setEnvironmentAudience] = (0, _react.useState)();
  const [isReadOnlyEnvironment, setIsReadOnlyEnvironment] = (0, _react.useState)(false);
  const [defaultEnvironmentTeachers, setDefaultEnvironmentTeachers] = (0, _react.useState)([]);
  const [srcEnvironmentId, setSrcEnvironmentId] = (0, _react.useState)();
  const teacherIds = (0, _react.useMemo)(() => environmentTeachers.map(t => t.UserID), [environmentTeachers]);
  const currentEnvironmentAudience = (0, _react.useMemo)(() => {
    return {
      selectedGroups: environmentAudience?.Groups?.map(g => {
        const groupData = userGroups.find(userGroup => userGroup.id === g);
        return groupData;
      }).filter(Boolean) ?? [],
      selectedStudents: environmentAudience?.SingleStudents ?? []
    };
  }, [environmentAudience?.Groups, environmentAudience?.SingleStudents, userGroups]);
  const audiencePickerProvider = (0, _useAudiencePickerProvider.useAudiencePickerProvider)({
    ...currentEnvironmentAudience,
    schoolId: environmentSchoolId ?? undefined,
    teacherIds,
    isReadOnly: isReadOnlyEnvironment || teacherIds.length <= 0,
    fullHeight: `calc(100vh - ${pxToRem(62)})`
  });
  const normalizeDate = (date, format) => {
    let normalizedDate = date ? new Date(date) : new Date();
    if (format === 'EndOfDay') {
      normalizedDate.setHours(23, 59, 59);
    } else {
      normalizedDate.setHours(0, 0, 0, 0);
    }
    return normalizedDate;
  };
  const resetEnvironment = () => {
    setEnvironmentIds({
      Id: '',
      AudienceID: ''
    });
    setEnvironmentTitle('');
    setEnvironmentTeachers(defaultEnvironmentTeachers);
    setEnvironmentDates({
      Start: new Date(),
      End: schoolYear?.endDate ? new Date(schoolYear.endDate) : new Date()
    });
    setEnvironmentAudience({
      Groups: [],
      SingleStudents: []
    });
    setIsReadOnlyEnvironment(false);
  };
  (0, _react.useEffect)(() => {
    if (userSessionData.role === 'admin') {
      setSliderView('schoolSelector');
    } else {
      setSliderView('main');
    }
  }, [userSessionData.role]);
  (0, _react.useEffect)(() => {
    if (audiencePickerProvider.view === 'createGroup') {
      setSliderView('createGroup');
    } else if (audiencePickerProvider.view === 'studentsSelector') {
      setSliderView('studentsSelector');
    } else if (audiencePickerProvider.view === 'groupEdit') {
      setSliderView('groupEdit');
    } else if (audiencePickerProvider.view === 'inline') {
      setSliderView('main');
    }
  }, [audiencePickerProvider.view, setSliderView]);
  (0, _react.useEffect)(() => {
    if (userSessionData.role === 'teacher' && userSessionData.userId && userSessionData.displayName) {
      setDefaultEnvironmentTeachers([{
        UserName: userSessionData.displayName,
        UserID: userSessionData.userId
      }]);
    }
  }, [userSessionData.role, userSessionData.userId, userSessionData.displayName]);
  const environmentStartDate = (0, _react.useMemo)(() => normalizeDate(environmentDates?.Start, 'StartOfDay'), [environmentDates?.Start]);
  const environmentEndDate = (0, _react.useMemo)(() => normalizeDate(environmentDates?.End, 'EndOfDay'), [environmentDates?.End]);
  const environmentGroups = (0, _react.useMemo)(() => [...audiencePickerProvider?.selectedClassesIds, ...audiencePickerProvider?.selectedGroupsIds].filter(g => g), [audiencePickerProvider?.selectedClassesIds, audiencePickerProvider?.selectedGroupsIds]);
  const environmentSingleStudents = (0, _react.useMemo)(() => audiencePickerProvider?.selectedStudentsIds?.map(studentId => {
    const studentInfo = audiencePickerProvider?.studentsMapper?.[studentId];
    return {
      id: studentId ?? '',
      fullName: studentInfo?.student?.fullName ?? '',
      UserID: studentId ?? '',
      ContextGroupID: studentInfo?.classIds?.length ? studentInfo?.classIds[0] : studentInfo?.groupIds?.length ? studentInfo?.groupIds[0] : null
    };
  }) ?? [], [audiencePickerProvider?.selectedStudentsIds, audiencePickerProvider?.studentsMapper]);
  const value = {
    userSessionData,
    audiencePickerProvider,
    getLabel,
    // UI Data
    sliderView,
    setSliderView,
    preloaderMode,
    setPreloaderMode,
    // API Data
    schoolYear,
    setSchoolYear,
    schoolTeachers,
    setSchoolTeachers,
    userGroups,
    setUserGroups,
    userEnvironments,
    setUserEnvironments,
    // Environment Data
    resetEnvironment,
    setSrcEnvironmentId,
    setEnvironmentIds,
    setEnvironmentTitle,
    setEnvironmentSchoolId,
    setEnvironmentDates,
    setEnvironmentProductInfo,
    setEnvironmentTeachers,
    setEnvironmentAudience,
    setIsReadOnlyEnvironment,
    defaultEnvironmentTeachers,
    srcEnvironmentId,
    environmentData: {
      Ids: environmentIds ?? {
        Id: '',
        AudienceID: ''
      },
      ProductInfo: environmentProductInfo ?? {},
      Title: environmentTitle,
      Dates: {
        Start: environmentStartDate,
        End: environmentEndDate
      },
      OwnerId: userSessionData.userId ?? '',
      SchoolID: environmentSchoolId ?? '',
      Teachers: environmentTeachers,
      Audience: {
        Groups: environmentGroups,
        SingleStudents: environmentSingleStudents
      },
      ScoreType: environmentProductInfo?.defaultScoreType ?? _ScoreTypes.ScoreTypes.Number,
      IsArchive: isReadOnlyEnvironment
    }
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(EnvironmentManagerContext.Provider, {
    value: value,
    children: children
  });
};
exports.EnvironmentManagerProvider = EnvironmentManagerProvider;